import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Search,
  Plane,
  Trash2,
  Edit,
  Download,
  RefreshCw,
  RotateCcw,
  Check,
  X
} from "lucide-react";

const SettingsAeropuertos = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [airports, setAirports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAirport, setEditingAirport] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [newAirport, setNewAirport] = useState({
    iata_code: "",
    name: "",
    city: "",
    country: ""
  });

  useEffect(() => {
    fetchAirports();
  }, [showInactive]);

  const fetchAirports = async () => {
    try {
      // Always fetch all airports and filter in frontend based on showInactive
      const res = await axios.get(`${API}/airports/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter based on showInactive: true = only inactive, false = only active
      const filtered = showInactive 
        ? res.data.filter(a => a.is_active === false)
        : res.data.filter(a => a.is_active !== false);
      setAirports(filtered);
    } catch (error) {
      console.error("Error fetching airports:", error);
      toast.error("Error al cargar aeropuertos");
    } finally {
      setLoading(false);
    }
  };

  const handleClearAndImport = async () => {
    if (!confirm("¿Estás seguro? Esto eliminará TODOS los aeropuertos y los volverá a importar.")) {
      return;
    }
    setImporting(true);
    try {
      // First clear all airports
      await axios.delete(`${API}/airports/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info("Aeropuertos eliminados, importando...");
      
      // Then import fresh data
      const res = await axios.post(`${API}/airports/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchAirports();
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Error al reimportar aeropuertos");
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/airports/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchAirports();
    } catch (error) {
      console.error("Error importing airports:", error);
      toast.error(error.response?.data?.detail || "Error al importar aeropuertos");
    } finally {
      setImporting(false);
    }
  };

  const handleAdd = async () => {
    if (!newAirport.iata_code || !newAirport.city || !newAirport.country) {
      toast.error("Código IATA, ciudad y país son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/airports`, newAirport, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aeropuerto agregado");
      setShowAddDialog(false);
      setNewAirport({ iata_code: "", name: "", city: "", country: "" });
      fetchAirports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar aeropuerto");
    }
  };

  const handleEdit = async () => {
    if (!editingAirport) return;
    try {
      await axios.put(`${API}/airports/${editingAirport.iata_code}`, {
        iata_code: editingAirport.iata_code,
        name: editingAirport.name,
        city: editingAirport.city,
        country: editingAirport.country
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aeropuerto actualizado");
      setShowEditDialog(false);
      setEditingAirport(null);
      fetchAirports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar aeropuerto");
    }
  };

  const handleDelete = async (iata_code) => {
    if (!confirm(`¿Inactivar aeropuerto ${iata_code}?`)) return;
    try {
      await axios.delete(`${API}/airports/${iata_code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aeropuerto inactivado");
      fetchAirports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al inactivar aeropuerto");
    }
  };

  const handleRestore = async (iata_code) => {
    try {
      await axios.post(`${API}/airports/restore/${iata_code}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aeropuerto restaurado");
      fetchAirports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restaurar aeropuerto");
    }
  };

  const filteredAirports = airports.filter(airport => {
    const search = searchTerm.toLowerCase();
    return (
      airport.iata_code?.toLowerCase().includes(search) ||
      airport.city?.toLowerCase().includes(search) ||
      airport.country?.toLowerCase().includes(search) ||
      airport.name?.toLowerCase().includes(search)
    );
  });

  const formatAirportDisplay = (airport) => {
    return (
      <span>
        <strong>({airport.iata_code})</strong> - {airport.city} - {airport.country} - <em className="text-slate-500">{airport.name}</em>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando aeropuertos...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="settings-aeropuertos-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Aeropuertos
              </h1>
              <p className="text-slate-500">Catálogo de aeropuertos IATA para cotizaciones de vuelos</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleImport}
              disabled={importing}
            >
              <Download className="w-4 h-4 mr-2" />
              {importing ? "Procesando..." : "Importar Nuevos"}
            </Button>
            <Button
              variant="destructive"
              onClick={handleClearAndImport}
              disabled={importing}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {importing ? "Procesando..." : "Limpiar y Reimportar"}
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Stats and Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 ${showInactive ? 'bg-red-50' : 'bg-blue-50'} px-4 py-2 rounded-lg`}>
                  <Plane className={`w-5 h-5 ${showInactive ? 'text-red-600' : 'text-blue-600'}`} />
                  <span className={`font-semibold ${showInactive ? 'text-red-900' : 'text-blue-900'}`}>{airports.length}</span>
                  <span className={showInactive ? 'text-red-600' : 'text-blue-600'}>
                    {showInactive ? 'aeropuertos inactivos' : 'aeropuertos activos'}
                  </span>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="rounded"
                  />
                  Mostrar solo inactivos
                </label>
              </div>
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por código, ciudad, país..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="airport-search"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Airports Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-slate-600" />
              Catálogo de Aeropuertos
            </CardTitle>
            <CardDescription>
              Formato: (CÓDIGO IATA) - Ciudad - País - <em>Nombre del aeropuerto</em>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border max-h-[600px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow>
                    <TableHead className="w-[100px]">Código</TableHead>
                    <TableHead>Ciudad</TableHead>
                    <TableHead>País</TableHead>
                    <TableHead>Nombre del Aeropuerto</TableHead>
                    <TableHead className="w-[100px]">Estado</TableHead>
                    <TableHead className="w-[100px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAirports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        {airports.length === 0 
                          ? "No hay aeropuertos. Haz clic en 'Importar IATA' para cargar el catálogo."
                          : "No se encontraron aeropuertos con ese criterio de búsqueda"
                        }
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAirports.slice(0, 200).map((airport) => (
                      <TableRow 
                        key={airport.iata_code}
                        className={airport.is_active === false ? "opacity-50 bg-slate-50" : ""}
                      >
                        <TableCell className="font-mono font-bold text-blue-600">
                          {airport.iata_code}
                        </TableCell>
                        <TableCell className="font-medium">{airport.city}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs bg-slate-100 px-1 rounded">{airport.country}</span>
                          {" - "}
                          <span>{airport.country_name || airport.country}</span>
                        </TableCell>
                        <TableCell className="italic text-slate-600">{airport.name}</TableCell>
                        <TableCell>
                          {airport.is_active === false ? (
                            <span className="flex items-center gap-1 text-xs text-red-600">
                              <X className="w-3 h-3" /> Inactivo
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <Check className="w-3 h-3" /> Activo
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingAirport(airport);
                                setShowEditDialog(true);
                              }}
                              className="h-8 w-8"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {airport.is_active === false ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRestore(airport.iata_code)}
                                className="h-8 w-8 text-green-600 hover:text-green-700"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(airport.iata_code)}
                                className="h-8 w-8 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {filteredAirports.length > 200 && (
              <p className="text-sm text-slate-500 mt-2 text-center">
                Mostrando 200 de {filteredAirports.length} aeropuertos. Usa el buscador para filtrar.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar Aeropuerto</DialogTitle>
              <DialogDescription>
                Ingresa los datos del nuevo aeropuerto
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Código IATA *</Label>
                <Input
                  value={newAirport.iata_code}
                  onChange={(e) => setNewAirport({...newAirport, iata_code: e.target.value.toUpperCase()})}
                  placeholder="MEX"
                  maxLength={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Ciudad *</Label>
                <Input
                  value={newAirport.city}
                  onChange={(e) => setNewAirport({...newAirport, city: e.target.value})}
                  placeholder="Ciudad de México"
                />
              </div>
              <div className="space-y-2">
                <Label>País *</Label>
                <Input
                  value={newAirport.country}
                  onChange={(e) => setNewAirport({...newAirport, country: e.target.value})}
                  placeholder="MX"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre del Aeropuerto</Label>
                <Input
                  value={newAirport.name}
                  onChange={(e) => setNewAirport({...newAirport, name: e.target.value})}
                  placeholder="Aeropuerto Internacional Benito Juárez"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAdd}>
                Agregar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Aeropuerto</DialogTitle>
              <DialogDescription>
                Modifica los datos del aeropuerto {editingAirport?.iata_code}
              </DialogDescription>
            </DialogHeader>
            {editingAirport && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Código IATA</Label>
                  <Input
                    value={editingAirport.iata_code}
                    disabled
                    className="bg-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ciudad *</Label>
                  <Input
                    value={editingAirport.city}
                    onChange={(e) => setEditingAirport({...editingAirport, city: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>País *</Label>
                  <Input
                    value={editingAirport.country}
                    onChange={(e) => setEditingAirport({...editingAirport, country: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre del Aeropuerto</Label>
                  <Input
                    value={editingAirport.name}
                    onChange={(e) => setEditingAirport({...editingAirport, name: e.target.value})}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEdit}>
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SettingsAeropuertos;
